import { useEffect, useState } from 'react'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { isCallCenterManagerRole, isManagerRole } from '@/utils/isManagerRole'
import { logError } from '@/utils/errorUtils'

/**
 * Checks whether the current user is a manager (care coordinator / call center
 * manager) by inspecting ALL of their PractitionerRole rows. Managers cannot
 * perform clinician write actions such as saving patient follow-up instructions.
 *
 * `isCallCenterManager` is the narrower flag (call-center manager only, care
 * coordinators excluded) for surfaces care coordinators may use, such as the
 * Default Pharmacy card (PD-1386).
 */
export function useIsManager(): {
  isManager: boolean
  isCallCenterManager: boolean
  loading: boolean
} {
  const medplum = useMedplum()
  const profile = useMedplumProfile()
  const [isManager, setIsManager] = useState(false)
  const [isCallCenterManager, setIsCallCenterManager] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile || profile.resourceType !== 'Practitioner') {
      setIsManager(false)
      setIsCallCenterManager(false)
      setLoading(false)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const roles = await medplum.searchResources(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        if (cancelled) return

        setIsManager(isManagerRole(roles))
        setIsCallCenterManager(isCallCenterManagerRole(roles))
      } catch (err) {
        logError('useIsManager', 'Role lookup failed', err)
        // If we can't determine the role, don't lock the user out of the feature.
        if (!cancelled) {
          setIsManager(false)
          setIsCallCenterManager(false)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [medplum, profile])

  return { isManager, isCallCenterManager, loading }
}
